import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { ArrowLeft, Save, CheckCircle2, Loader2, Users } from 'lucide-react';
import { toast } from 'sonner';
import { onboardingAPI } from '../../lib/onboardingAPI';

export default function SpouseForm({ clientId }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasSpouse, setHasSpouse] = useState('no');
  const [f, setF] = useState({
    first_name: '', last_name: '', date_of_birth: '', email: '', mobile: '', tfn: '',
    occupation: '', taxable_income_estimate: '', medicare_details: '',
    private_health_insurance: false, requires_tax_return: false, send_registration_link: false,
  });

  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const onInput = (k) => (e) => set(k)(e.target.value);

  useEffect(() => {
    if (!clientId) return;
    onboardingAPI.getSection(clientId, 'spouse')
      .then((res) => {
        const rd = res.data.response_data || {};
        if (typeof rd.has_spouse === 'boolean') setHasSpouse(rd.has_spouse ? 'yes' : 'no');
        setF((s) => ({ ...s, ...rd, taxable_income_estimate: rd.taxable_income_estimate ?? '' }));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [clientId]);

  const save = async (status) => {
    const yes = hasSpouse === 'yes';
    if (yes && (!f.first_name || !f.last_name)) {
      toast.error('Please enter your spouse\'s first and last name');
      return;
    }
    setSaving(true);
    try {
      await onboardingAPI.saveSpouse(clientId, {
        has_spouse: yes,
        status,
        first_name: f.first_name || null,
        last_name: f.last_name || null,
        date_of_birth: f.date_of_birth || null,
        email: f.email || null,
        mobile: f.mobile || null,
        tfn: f.tfn || null,
        occupation: f.occupation || null,
        taxable_income_estimate: f.taxable_income_estimate ? Number(f.taxable_income_estimate) : null,
        medicare_details: f.medicare_details || null,
        private_health_insurance: !!f.private_health_insurance,
        requires_tax_return: !!f.requires_tax_return,
        send_registration_link: !!f.send_registration_link,
      });
      toast.success(yes ? 'Spouse details saved — a linked record was created in your family group' : 'Saved');
      navigate('/onboarding');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-6"><Loader2 className="w-6 h-6 animate-spin text-foreground" /></div>;
  const yes = hasSpouse === 'yes';

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6">
      <Button variant="ghost" onClick={() => navigate('/onboarding')} className="text-muted-foreground -ml-2">
        <ArrowLeft className="w-4 h-4 mr-2" /> Back to checklist
      </Button>

      <Card>
        <CardHeader>
          <CardTitle className="text-2xl text-foreground" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Spouse Details</CardTitle>
          <CardDescription>Do you have a spouse or de facto partner?</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <RadioGroup value={hasSpouse} onValueChange={setHasSpouse} className="flex gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <RadioGroupItem value="yes" id="spouse-yes" data-testid="spouse-yes" /> Yes
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <RadioGroupItem value="no" id="spouse-no" data-testid="spouse-no" /> No
            </label>
          </RadioGroup>

          {yes && (
            <>
              <div className="flex items-start gap-2 text-xs text-blue-800 bg-blue-50 border border-blue-200 rounded-lg p-3">
                <Users className="w-4 h-4 mt-0.5 flex-shrink-0" />
                A linked client record will be created for your spouse and added to your family group.
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Spouse first name *</Label><Input value={f.first_name} onChange={onInput('first_name')} data-testid="spouse-first" /></div>
                <div className="space-y-2"><Label>Spouse last name *</Label><Input value={f.last_name} onChange={onInput('last_name')} data-testid="spouse-last" /></div>
                <div className="space-y-2"><Label>Date of birth</Label><Input type="date" value={f.date_of_birth || ''} onChange={onInput('date_of_birth')} /></div>
                <div className="space-y-2"><Label>TFN</Label><Input value={f.tfn || ''} onChange={onInput('tfn')} /></div>
                <div className="space-y-2"><Label>Email</Label><Input type="email" value={f.email || ''} onChange={onInput('email')} /></div>
                <div className="space-y-2"><Label>Mobile</Label><Input value={f.mobile || ''} onChange={onInput('mobile')} /></div>
                <div className="space-y-2"><Label>Occupation</Label><Input value={f.occupation || ''} onChange={onInput('occupation')} /></div>
                <div className="space-y-2"><Label>Taxable income estimate</Label><Input type="number" value={f.taxable_income_estimate} onChange={onInput('taxable_income_estimate')} /></div>
                <div className="space-y-2 md:col-span-2"><Label>Medicare details</Label><Input value={f.medicare_details || ''} onChange={onInput('medicare_details')} /></div>
              </div>
              <div className="space-y-2">
                {[
                  ['private_health_insurance', 'Spouse has private health insurance'],
                  ['requires_tax_return', 'Spouse also requires a tax return'],
                ].map(([k, label]) => (
                  <label key={k} className="flex items-center gap-2 rounded-lg border p-3 cursor-pointer hover:bg-muted/50">
                    <Checkbox checked={!!f[k]} onCheckedChange={(v) => set(k)(!!v)} data-testid={`spouse-${k}`} />
                    <span className="text-sm text-foreground/90">{label}</span>
                  </label>
                ))}
              </div>
            </>
          )}

          <div className="flex justify-between pt-2">
            <Button variant="outline" onClick={() => save('in_progress')} disabled={saving} data-testid="spouse-save">
              <Save className="w-4 h-4 mr-2" /> Save &amp; return later
            </Button>
            <Button onClick={() => save('completed')} disabled={saving} data-testid="spouse-complete">
              <CheckCircle2 className="w-4 h-4 mr-2" /> Mark as complete
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
